/**
 * SEPM OAuth2 authorization-code + PKCE for the documentation site.
 * Public client, same pattern as My Account and the product site.
 */

const PKCE_STORAGE_KEY = "sepm_docs_pkce";
const RETURN_STORAGE_KEY = "sepm_docs_return_to";

export const ACCOUNTS_BASE_URL = (
  process.env.REACT_APP_ACCOUNTS_BASE_URL || "https://accounts.sepm.ch"
).replace(/\/$/, "");

export const OAUTH_CLIENT_ID = process.env.REACT_APP_OAUTH_CLIENT_ID || "sepm_docs";

export const OAUTH_SCOPE = process.env.REACT_APP_OAUTH_SCOPE || "read profile";

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(["live", "trial", "non_renewing", "dunning"]);

function base64UrlEncode(buffer) {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : buffer;
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomUrlSafeString(byteLength = 32) {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Base64Url(plain) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(plain));
  return base64UrlEncode(digest);
}

export function callbackRedirectUri() {
  return `${window.location.origin}/auth/callback`;
}

export function safeReturnPath(value) {
  if (!value || typeof value !== "string") return "/";
  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  if (value.startsWith("/login") || value.startsWith("/auth/callback")) return "/";
  return value;
}

export function stashReturnPath(path) {
  sessionStorage.setItem(RETURN_STORAGE_KEY, safeReturnPath(path));
}

export function peekReturnPath() {
  return safeReturnPath(sessionStorage.getItem(RETURN_STORAGE_KEY));
}

export function clearReturnPath() {
  sessionStorage.removeItem(RETURN_STORAGE_KEY);
}

export async function startSepmSignIn({ theme, returnTo, lang } = {}) {
  if (returnTo) stashReturnPath(returnTo);
  const codeVerifier = randomUrlSafeString(64);
  const codeChallenge = await sha256Base64Url(codeVerifier);
  const state = randomUrlSafeString(24);
  const redirectUri = callbackRedirectUri();

  sessionStorage.setItem(
    PKCE_STORAGE_KEY,
    JSON.stringify({
      codeVerifier,
      state,
      redirectUri,
      createdAt: Date.now(),
    })
  );

  const params = new URLSearchParams({
    response_type: "code",
    client_id: OAUTH_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: OAUTH_SCOPE,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  if (theme === "light" || theme === "dark") params.set("theme", theme);
  if (lang === "en" || lang === "de" || lang === "fr") params.set("lang", lang);

  window.location.assign(`${ACCOUNTS_BASE_URL}/oauth2/authorize?${params.toString()}`);
}

function readPkceSession() {
  try {
    const raw = sessionStorage.getItem(PKCE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearPkceSession() {
  sessionStorage.removeItem(PKCE_STORAGE_KEY);
}

const exchangeInflight = new Map();

async function exchangeCodeForTokens({ code, state }) {
  const pkce = readPkceSession();
  if (!pkce?.codeVerifier || !pkce?.state) {
    throw new Error("Missing sign-in session. Please start again.");
  }
  if (pkce.state !== state) {
    clearPkceSession();
    throw new Error("Sign-in could not be verified. Please start again.");
  }

  const response = await fetch(`${ACCOUNTS_BASE_URL}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: pkce.redirectUri || callbackRedirectUri(),
      client_id: OAUTH_CLIENT_ID,
      code_verifier: pkce.codeVerifier,
    }).toString(),
  });
  const data = await response.json().catch(() => ({}));
  clearPkceSession();
  if (!response.ok || !data.access_token) {
    const detail = data?.detail || data?.error_description || data?.error || "Sign-in failed.";
    throw new Error(typeof detail === "string" ? detail : "Sign-in failed.");
  }
  return data;
}

/** Authorization codes are single-use. Strict Mode can run the callback twice. */
export function exchangeCodeForTokensOnce(args) {
  const key = args.code;
  if (!exchangeInflight.has(key)) {
    exchangeInflight.set(key, exchangeCodeForTokens(args));
  }
  return exchangeInflight.get(key);
}

export async function refreshAccessToken(refreshToken) {
  const response = await fetch(`${ACCOUNTS_BASE_URL}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_id: OAUTH_CLIENT_ID,
    }).toString(),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.access_token) {
    throw new Error("Session expired.");
  }
  return data;
}

export async function fetchUserInfo(accessToken) {
  const response = await fetch(`${ACCOUNTS_BASE_URL}/oauth2/userinfo`, {
    headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.email_id) {
    const error = new Error("Account details could not be loaded.");
    error.status = response.status;
    throw error;
  }
  return {
    userId: data.user_id,
    email: data.email_id,
    firstName: data.first_name || "",
    lastName: data.last_name || "",
    profilePic: data.profile_pic_path || "",
    preferredLanguage: data.preferred_language || "",
  };
}

export async function savePreferredLanguage(accessToken, language) {
  const response = await fetch(`${ACCOUNTS_BASE_URL}/oauth2/preferences`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ preferred_language: language }),
  });
  return response.ok;
}

function productIsActive(product) {
  if (!product) return false;
  const status = String(product.subscription?.status || "").toLowerCase();
  if (ACTIVE_SUBSCRIPTION_STATUSES.has(status)) return true;
  return (product.features || []).length > 0 || (product.formats || []).length > 0;
}

/**
 * A user can read the X-Translator manual when they are seated on a live
 * X-Translator subscription, or they own an organisation whose X-Translator
 * subscription is still active.
 */
export async function fetchXTranslatorAccess(accessToken, userId) {
  const headers = { Authorization: `Bearer ${accessToken}`, Accept: "application/json" };
  const [basicRes, statusRes] = await Promise.all([
    fetch(
      `${ACCOUNTS_BASE_URL}/user/${userId}/basic-info?product=${encodeURIComponent("X-Translator")}`,
      { headers }
    ),
    fetch(`${ACCOUNTS_BASE_URL}/subscription/self-serve/status`, { headers }),
  ]);

  if ((basicRes.status === 401 || statusRes.status === 401) && !basicRes.ok && !statusRes.ok) {
    const error = new Error("unauthorized");
    error.status = 401;
    throw error;
  }

  const basic = basicRes.ok ? await basicRes.json().catch(() => null) : null;
  const statusBody = statusRes.ok ? await statusRes.json().catch(() => null) : null;

  let seated = null;
  for (const org of basic?.organisations || []) {
    const product = org?.products?.["X-Translator"];
    if (productIsActive(product)) {
      seated = product.subscription || { status: "live" };
      break;
    }
  }

  const ownerStatus = String(statusBody?.subscription?.status || "").toLowerCase();
  const ownerActive = statusRes.ok && ACTIVE_SUBSCRIPTION_STATUSES.has(ownerStatus);
  if (!basicRes.ok && !ownerActive) {
    const error = new Error("Could not check the X-Translator subscription.");
    error.status = basicRes.status === 401 ? 401 : 503;
    throw error;
  }
  const subscription = seated || (ownerActive ? statusBody.subscription : null);

  return {
    active: Boolean(seated) || ownerActive,
    planName: subscription?.plan_name || subscription?.name || "",
    status: String(subscription?.status || "").toLowerCase(),
    checkedAt: Date.now(),
  };
}

export function signOutAndReturn(accessToken) {
  const params = new URLSearchParams({
    return_url: `${window.location.origin}/login`,
  });
  if (accessToken) params.set("access_token", accessToken);
  window.location.assign(`${ACCOUNTS_BASE_URL}/user/logout?${params.toString()}`);
}
